import React, { useEffect, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

export type ManifestColumn = { label: string; numeric?: boolean };
type ManifestCell = { text: string; href?: string };
export type ManifestTableRow = { key: string; cells: Array<ManifestCell> };

type Props = {
  title: string;
  caption: string;
  columns: Array<ManifestColumn>;
  rows: Array<ManifestTableRow>;
  totals?: Array<string>;
  onCsv: () => void;
};

const PAGE_SIZES = [50, 100, 500];

export default function LtlpManifestTable({ title, caption, columns, rows, totals, onCsv }: Props) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  useEffect(() => setPage(0), [rows]);
  const visible = rows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <Paper variant="outlined" sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        sx={{ p: 2, pb: 1 }}
      >
        <Stack>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        </Stack>
        <Button size="small" variant="text" onClick={onCsv} disabled={!rows.length}>
          Download CSV
        </Button>
      </Stack>
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table size="small" stickyHeader aria-label={title}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.label} align={column.numeric ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => (
              <TableRow key={row.key} hover>
                {row.cells.map((cell, index) => (
                  <TableCell
                    key={columns[index].label}
                    align={columns[index].numeric ? 'right' : 'left'}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: columns[index].numeric ? 'tabular-nums' : undefined,
                    }}
                  >
                    {cell.href ? (
                      <Link component={RouterLink} to={cell.href}>
                        {cell.text}
                      </Link>
                    ) : (
                      cell.text
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    No rows for these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {totals && rows.length > 0 && (
              <TableRow>
                {totals.map((text, index) => (
                  <TableCell
                    key={columns[index].label}
                    align={columns[index].numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {text}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={PAGE_SIZES}
        onRowsPerPageChange={(event) => {
          setPageSize(Number(event.target.value));
          setPage(0);
        }}
      />
    </Paper>
  );
}
